import { extractSecretOps } from "./index.js";
import { collectWindowOps, groupActivity, distinctKey } from "../../src/build/index.js";

export const extract = extractSecretOps;
// A denial is the MOST informed sighting, not the least: it is the finding.
const RANK = { "denied-by-harness": 4, "refused-by-provider": 3, granted: 2, unrecorded: 1 };
export const informedness = (op) => (RANK[op.outcome] || 0) * 100 + (op.secret ? 1 : 0);
export const collect = (prompts) => collectWindowOps(prompts, { informedness });

// The secret's name in its store. An enumeration names none, and keys on the store
// itself — asking "what secrets exist here" is one coherent act.
export const groupBy = (op) => {
  if (op.secret) return `${op.store}/${op.secret}`;
  if (op.kind === "enumerate") return `${op.store} (enumerated)`;
  // An unrecoverable name must NOT get a distinct key. A secret addressed through
  // a variable assigned in an earlier CALL cannot be resolved from this one, and
  // giving each such op its own key rendered 53 singleton rows all reading "an
  // unnamed secret" — every one of them the same act against the same store. One
  // row that says the name is not recoverable is the honest reading.
  return `${op.store} (name not recoverable)`;
};

// A RARE-SIGNAL LENS MUST LEAD WITH ITS FINDINGS, not with its most recent rows.
// On a real scope this rendered two reads confirming an agent still had a PAT, while
// three harness-blocked credential enumerations sat elsewhere in the same corpus.
// Showing the common case first is worse than showing nothing: it implies the
// interesting states are absent when they were only outside the window.
const RANK_GROUP = (g) => (g.blocked ? 3 : 0) + (g.leaks ? 2 : 0)
  + (g.outcomes?.["refused-by-provider"] ? 1 : 0);

export const build = (flat) => {
  const built = grouped(flat);
  built.groups.sort((a, b) => RANK_GROUP(b) - RANK_GROUP(a) || (b.lastTs ?? 0) - (a.lastTs ?? 0));
  // Within a group too: a blocked read is the row to read first.
  for (const g of built.groups) {
    g.ops.sort((x, y) => outcomeRank(y.outcome) - outcomeRank(x.outcome));
  }
  built.findings = built.groups.filter((g) => RANK_GROUP(g) > 0).length;
  return built;
};

const outcomeRank = (o) => (o === "denied-by-harness" ? 3 : o === "refused-by-provider" ? 2 : o === "granted" ? 1 : 0);

const grouped = (flat) => groupActivity(flat, {
  key: groupBy,
  label: (op) => {
    if (op.byReference) return `${op.secret} (named by the agent's own variable)`;
    if (op.secret) return op.secret;
    if (op.kind === "enumerate") return `every secret in ${op.store}`;
    return `${op.store} — reads whose secret name is not in the trace`;
  },
  seed: () => ({ kinds: {}, outcomes: {}, labels: [], stores: [], regions: [], leaks: 0, blocked: 0 }),
  fold: (g, op) => {
    g.kinds[op.kind] = (g.kinds[op.kind] || 0) + 1;
    g.outcomes[op.outcome] = (g.outcomes[op.outcome] || 0) + 1;
    if (op.valueReachedTrace) g.leaks += 1;
    if (op.outcome === "denied-by-harness") g.blocked += 1;
    if (!g.stores.includes(op.store)) g.stores.push(op.store);
    if (op.region && !g.regions.includes(op.region)) g.regions.push(op.region);
  },
  finish: (g) => {
    g.labels = [
      g.blocked ? `${g.blocked} blocked by the harness` : null,
      g.leaks ? `value in trace ×${g.leaks}` : null,
      ...g.stores, ...g.regions.slice(0, 2),
    ].filter(Boolean);
  },
});

export const anchor = () => "secret";
export const ignore = ["output", "steps", "checks"];
